import type { TAuthConfig } from 'react-oauth2-code-pkce';
import servers from '../../utils/servers';
import { storeAuthToken } from '../../cookies/authStorage';
import config, { ConfigKey } from '../../utils/config';
import RocpStorageKey from './enums';
import { redirectToLogin } from './authRedirect';
import type { OAuthState } from './types';

const DEFAULT_REDIRECT_PATH = '/dashboard';

/**
 * Safely extracts redirect path from OAuth state in localStorage.
 * Returns empty string if state is null, invalid JSON, or missing redirectPath.
 */
const getRedirectPath = (): string => {
  const authStateJson = localStorage.getItem(RocpStorageKey.AUTH_STATE);
  if (!authStateJson) return DEFAULT_REDIRECT_PATH;

  try {
    const authState = JSON.parse(authStateJson) as OAuthState;
    return authState?.redirectPath || DEFAULT_REDIRECT_PATH;
  } catch {
    return DEFAULT_REDIRECT_PATH;
  }
};

const getOAuthConfig = (): TAuthConfig => {
  const ssoEnabled = config.get(ConfigKey.SSO_ENABLED) === 'true';

  return {
    clientId: servers.uid,
    authorizationEndpoint: ssoEnabled ? `${servers.api}/sso_login` : `${servers.api}/login`,
    tokenEndpoint: `${servers.api}/oauth/token`,
    logoutEndpoint: `${servers.api}/logout`,
    redirectUri: `${window.location.origin}/auth/callback`,
    logoutRedirect: `${window.location.origin}`,
    // Core does not support scopes
    scope: 'basic',
    // Core does not issue JWT tokens so we don't need to decode them
    decodeToken: false,
    // State is set dynamically in useLogIn.ts but we still set a default state for cases
    // where useLogIn isn't used (e.g. hitting /auth/callback directly when logged out)
    state: JSON.stringify({ csrf: crypto.randomUUID(), redirectPath: '' } as OAuthState),
    extraAuthParameters: {
      // Core requires this because of backwards compatibility with the previous auth flow
      use_pkce: true,
    },
    postLogin: () => {
      const token = localStorage.getItem(RocpStorageKey.TOKEN);
      storeAuthToken(token);

      window.location.href = `${window.location.origin}${getRedirectPath()}`;
    },
    onRefreshTokenExpire: () => {
      redirectToLogin();
    },
  };
};

export default getOAuthConfig;
